import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Moon, Sun, Star, Menu, X} from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-slate-200 font-sans selection:bg-[#D4AF37] selection:text-black">
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-tr from-[#D4AF37] to-[#FCD34D] text-[#0B0E14]">
                <Star className="w-6 h-6 fill-current" />
                <div className="absolute inset-0 rounded-full bg-[#D4AF37] blur-md opacity-40 group-hover:opacity-70 transition-opacity" />
              </div>
              <span className="text-xl font-serif font-bold text-white tracking-tight">
                Ascended<span className="text-[#D4AF37]">Calculators</span>
              </span>
            </Link>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-6">
                <NavLink to="/" active={isActive('/')}>Calculator</NavLink>
                <NavLink to="/big-3-guide" active={isActive('/big-3-guide')}>Big 3 Guide</NavLink>
                <NavLink to="/daily-horoscope" active={isActive('/daily-horoscope')}>Daily Horoscope</NavLink>
                <NavLink to="/horoscope-rising-sign" active={isActive('/horoscope-rising-sign')}>Rising Sign Guide</NavLink>
                <NavLink to="/zodiac-signs-rising-sign" active={isActive('/zodiac-signs-rising-sign')}>All 12 Signs</NavLink>
                <NavLink to="/faq" active={isActive('/faq')}>FAQ</NavLink>
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-300 hover:text-white p-2"
              >
                {isMenuOpen ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0B0E14] border-b border-white/5"
            >
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                <MobileNavLink to="/" onClick={() => setIsMenuOpen(false)}>Calculator</MobileNavLink>
                <MobileNavLink to="/big-3-guide" onClick={() => setIsMenuOpen(false)}>Big 3 Guide</MobileNavLink>
                <MobileNavLink to="/daily-horoscope" onClick={() => setIsMenuOpen(false)}>Daily Horoscope</MobileNavLink>
                <MobileNavLink to="/horoscope-rising-sign" onClick={() => setIsMenuOpen(false)}>Rising Sign Guide</MobileNavLink>
                <MobileNavLink to="/zodiac-signs-rising-sign" onClick={() => setIsMenuOpen(false)}>All 12 Signs</MobileNavLink>
                <MobileNavLink to="/faq" onClick={() => setIsMenuOpen(false)}>FAQ</MobileNavLink>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="pt-20">
        {children}
      </main>

      <footer className="bg-[#05080C] border-t border-white/5 mt-20 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-serif font-bold text-white mb-4">Ascended Calculators</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Your definitive source for accurate astrological calculations. 
                Discover your Sun, Moon, and Rising signs with precision and clarity.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-serif font-bold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/" className="hover:text-[#D4AF37] transition-colors">Rising Sign Calculator</Link></li>
                <li><Link to="/big-3-guide" className="hover:text-[#D4AF37] transition-colors">Understanding the Big 3</Link></li>
                <li><Link to="/daily-horoscope" className="hover:text-[#D4AF37] transition-colors">Daily Horoscope</Link></li>
                <li><Link to="/horoscope-rising-sign" className="hover:text-[#D4AF37] transition-colors">Horoscope Rising Sign Guide</Link></li>
                <li><Link to="/zodiac-signs-rising-sign" className="hover:text-[#D4AF37] transition-colors">All 12 Zodiac Rising Signs</Link></li>
                <li><Link to="/faq" className="hover:text-[#D4AF37] transition-colors">Astrology FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-serif font-bold text-white mb-4">Connect</h3>
              <p className="text-gray-400 text-sm">
                Explore the cosmos within yourself.
              </p>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/5 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Ascended Calculators. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavLink({ to, children, active }: { to: string; children: ReactNode; active: boolean }) {
  return (
    <Link
      to={to}
      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
        active 
          ? 'text-[#D4AF37] bg-white/5' 
          : 'text-gray-300 hover:text-white hover:bg-white/5'
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, children, onClick }: { to: string; children: ReactNode; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
    >
      {children}
    </Link>
  );
}
